// Feature: rule profile viewer (owner: shell work package).
// Contract: export PolicyPage.
//
// Shows the policy version the server applies, every parameter with its value and
// status, and what stays blocked while a value is unresolved. Synthetic and pending
// values are never presented as approved policy. From 0023 also the version's lifecycle
// (candidate, frozen, approved, active, withdrawn), its recorded review and approval, its
// activations and the implementation-capability check; administration (PolicyAdmin) only
// for a person the server says holds administer_policy. Read-only for everyone else.
import { useId, useState } from 'react'
import type { PolicyEvidence } from '@/domain/types'
import { ErrorBanner, RuleRefs } from '@/components/evidence'
import { Badge, Banner, Card, EmptyState, Field, KeyValue, PageHeader, SectionTitle, Select, Spinner } from '@/components/ui'
import { fmtDateTime, shortDigest } from '@/lib/format'
import { PtwError } from '@/lib/commands'
import { useMe, usePolicy } from '@/lib/queries'
import { PolicyAdmin } from './PolicyAdmin'
import {
  approvalSentence,
  blockedWhileUnresolved,
  blockerTitle,
  implementedRouteNote,
  lifecycleInfo,
  supersededNote,
  versionLifecycle,
  formatPolicyValue,
  groupParameters,
  permitFamilyLabel,
  type PolicyParameter,
  recordModeLabel,
  STATUS_ORDER,
  statusCounts,
  statusInfo,
  supportedClassesText,
  VALUE_STATUS_INFO,
} from './policyModel'

export function PolicyPage() {
  const [versionId, setVersionId] = useState<string | null>(null)
  const q = usePolicy(versionId)
  const me = useMe()
  const [status, setStatus] = useState('')
  const filterId = useId()

  if (q.isLoading) {
    return (
      <div className="flex justify-center p-8">
        <Spinner />
      </div>
    )
  }
  if (!q.data) {
    if (versionId === null && isNoVersion(q.error)) {
      return <NoVersionInForce canAdminister={(me.data?.global_capabilities ?? []).includes('administer_policy')} onView={setVersionId} />
    }
    return <ErrorBanner error={q.error ?? new Error('The rules are not available.')} title="Could not load the rules" />
  }

  const pv = q.data
  const versionStatus = statusInfo(pv.version.status)
  const counts = statusCounts(pv.parameters)
  const shown = status ? pv.parameters.filter((p) => p.value_status === status) : pv.parameters
  const groups = groupParameters(shown)
  const blocked = pv.parameters.filter((p) => blockedWhileUnresolved(p) !== null).sort((a, b) => a.parameter_code.localeCompare(b.parameter_code))
  const supported = supportedClassesText(pv.parameters)
  const lifecycle = versionLifecycle(pv)
  const life = lifecycleInfo(lifecycle)
  const routeNote = implementedRouteNote(pv.parameters)

  return (
    <div className="space-y-4">
      <PageHeader title="Rules in use" subtitle="The rule values the server applies at every step, with their status and source references." />

      {q.isError ? (
        <Banner tone="warn" title="Showing the last copy loaded">
          Connection problem. These rules were loaded at {fmtDateTime(new Date(q.dataUpdatedAt).toISOString())}.
        </Banner>
      ) : null}

      {lifecycle === 'active' ? (
        <Banner tone="ok" title="Active rule version">
          Approved and active for new permits. Effective from {fmtDateTime(pv.version.effective_from)}. Any value below that is not
          approved still blocks the step it governs.
        </Banner>
      ) : lifecycle === 'synthetic' ? (
        <Banner tone="synthetic" title="Not approved policy">
          <p>
            {pv.profile.name}, version <span className="code font-semibold">{pv.version.version_label}</span>, status{' '}
            <span className="font-semibold">{versionStatus.label}</span>. It exists for the demonstration only.
          </p>
          <p className="mt-1">
            Pending approval values were checked against PDO SP-2329 but are not adopted by BadrEOR. Synthetic values exist only so
            the demo can run. Unresolved values block the steps they govern.
          </p>
        </Banner>
      ) : (
        <Banner tone={life.tone === 'bad' ? 'bad' : 'warn'} title={life.label}>
          {life.explanation}
        </Banner>
      )}

      <Card>
        <SectionTitle>Profile and version</SectionTitle>
        <KeyValue
          items={[
            ['Profile', pv.profile.name],
            ['Profile code', <span className="code">{pv.profile.code}</span>],
            ['Version', <span className="code">{pv.version.version_label}</span>],
            ['Status', <Badge tone={life.tone}>{life.label}</Badge>],
            ['Effective from', fmtDateTime(pv.version.effective_from)],
            ...(pv.version.effective_to ? ([['Effective to', fmtDateTime(pv.version.effective_to)]] as Array<[string, string]>) : []),
            ...(pv.version.approval_evidence_reference
              ? ([['Approval evidence', pv.version.approval_evidence_reference]] as Array<[string, string]>)
              : []),
            [
              'Permit family',
              <>
                {permitFamilyLabel(pv.profile.permit_family)}
                {supported ? <span className="block text-ink-soft">Built in this version: {supported} only</span> : null}
              </>,
            ],
            ['System of record', recordModeLabel(pv.profile.system_of_record_mode)],
            ['Rules fingerprint', <span className="code">{shortDigest(pv.version.rules_digest)}</span>],
          ]}
        />
        <div className="mt-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-ink-faint">Source baseline</p>
          <p className="mt-0.5 text-sm text-ink">{pv.version.source_baseline}</p>
        </div>
      </Card>

      <ApprovalCard pv={pv} />
      {pv.capability_check ? <CapabilityCard check={pv.capability_check} /> : null}
      {pv.can_administer ? <PolicyAdmin onView={setVersionId} viewingId={pv.version.id} /> : null}

      <Card>
        <SectionTitle>What the statuses mean</SectionTitle>
        <ul className="space-y-3">
          {STATUS_ORDER.map((s) => (
            <li key={s} className="text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={VALUE_STATUS_INFO[s].tone}>{VALUE_STATUS_INFO[s].label}</Badge>
                <span className="text-xs text-ink-soft">
                  {counts[s] ?? 0} {(counts[s] ?? 0) === 1 ? 'value' : 'values'}
                </span>
              </div>
              <p className="mt-0.5 text-ink">{VALUE_STATUS_INFO[s].explanation}</p>
            </li>
          ))}
        </ul>
        <p className="mt-3 text-xs text-ink-soft">
          Rule IDs (CORE-, GOV-, ACT-, SYS-) point to the research register. Decision IDs (DEC-) are open questions that still
          need an owner&apos;s answer. Neither is adopted BadrEOR policy.
        </p>
      </Card>

      {blocked.length > 0 ? (
        <Card>
          <SectionTitle aside={<Badge tone="bad">{blocked.length} unresolved</Badge>}>Stays blocked until decided</SectionTitle>
          <ul className="space-y-2">
            {blocked.map((p) => (
              <li key={p.parameter_code} className="text-sm text-ink">
                <span className="code break-all font-semibold">{p.parameter_code}</span>: {blockedWhileUnresolved(p)}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <div className="w-full sm:w-80">
        <Field label="Show" htmlFor={filterId}>
          <Select
            id={filterId}
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            options={STATUS_ORDER.map((s) => ({ value: s, label: `${VALUE_STATUS_INFO[s].label} (${counts[s] ?? 0})` }))}
            placeholder={`All values (${pv.parameters.length})`}
          />
        </Field>
      </div>

      {groups.length === 0 ? (
        <EmptyState title="No values with this status" />
      ) : (
        groups.map((g) => (
          <Card key={g.key}>
            <SectionTitle aside={<span className="code text-xs text-ink-faint">{g.key}</span>}>{g.title}</SectionTitle>
            {g.key === 'routes' && routeNote ? (
              <p className="mb-3 rounded-lg border border-info/30 bg-info-light p-2 text-sm text-ink">{routeNote}</p>
            ) : null}
            <ul className="divide-y divide-line">
              {g.params.map((p) => (
                <ParamRow key={p.parameter_code} p={p} />
              ))}
            </ul>
          </Card>
        ))
      )}
    </div>
  )
}

/** The server found no rule version for the default view (PW005): nothing is in force yet. */
function isNoVersion(error: unknown): boolean {
  return error instanceof PtwError && error.code === 'PW005'
}

/**
 * A new environment has no rule version in force at any site the person can view. That is
 * a state, not a failure; a rulebook administrator gets the panel to create the first one.
 */
export function NoVersionInForce({ canAdminister, onView }: { canAdminister: boolean; onView: (id: string | null) => void }) {
  return (
    <div className="space-y-4">
      <PageHeader title="Rules in use" subtitle="The rule values the server applies at every step, with their status and source references." />
      <EmptyState title="No rule version in force yet">
        No permit can be raised until a rule version is approved and activated for a site.
      </EmptyState>
      {canAdminister ? <PolicyAdmin onView={onView} viewingId={null} /> : null}
    </div>
  )
}

function ParamRow({ p }: { p: PolicyParameter }) {
  const info = statusInfo(p.value_status)
  const value = formatPolicyValue(p.value)
  const blocked = blockedWhileUnresolved(p)
  const superseded = supersededNote(p)
  const hasRefs = p.rule_ids.length > 0 || p.decision_ids.length > 0
  return (
    <li className="py-3 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="code break-all text-sm font-semibold text-ink">{p.parameter_code}</span>
        <Badge tone={info.tone}>{info.label}</Badge>
      </div>
      {value === null ? (
        <p className="mt-1 text-sm font-semibold text-bad">No value</p>
      ) : (
        <pre className="code mt-1 max-h-72 overflow-auto whitespace-pre-wrap break-words rounded-lg border border-line bg-paper p-2 text-xs text-ink">
          {value}
        </pre>
      )}
      {blocked ? (
        <p className="mt-2 rounded-lg border border-bad/30 bg-bad-light p-2 text-sm text-ink">
          <span className="font-semibold text-bad">Stays blocked: </span>
          {blocked}
        </p>
      ) : null}
      {p.note ? <p className="mt-1 text-sm text-ink-soft">{p.note}</p> : null}
      {superseded ? <p className="mt-1 text-sm font-semibold text-info">{superseded}</p> : null}
      {p.source_reference ? (
        <p className="mt-1 text-sm text-ink">
          <span className="font-semibold">Approved source: </span>
          {p.source_reference}
        </p>
      ) : null}
      <p className="mt-1 text-xs text-ink-soft">
        {hasRefs ? (
          <>
            References:
            <RuleRefs ruleIds={p.rule_ids} decisionIds={p.decision_ids} />
          </>
        ) : (
          'No rule reference'
        )}
      </p>
    </li>
  )
}

type Evidence = Partial<PolicyEvidence>

/** Freeze, recorded review and approval, and activations: who decided, on what, from when. */
function ApprovalCard({ pv }: { pv: Evidence }) {
  const approvals = pv.approvals ?? []
  const activations = pv.activations ?? []
  return (
    <Card>
      <SectionTitle>Approval and activation</SectionTitle>
      {pv.freeze ? (
        <p className="text-sm text-ink">
          Frozen for approval at {fmtDateTime(pv.freeze.frozen_at)} by {pv.freeze.frozen_by} ({pv.freeze.parameter_count} values, fingerprint{' '}
          <span className="code">{shortDigest(pv.freeze.rules_digest)}</span>).
        </p>
      ) : (
        <p className="text-sm text-ink-soft">Not frozen for approval. A version is reviewed and approved only after its values are frozen.</p>
      )}
      <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-ink-faint">Review and approval</p>
      {approvals.length === 0 ? (
        <p className="mt-1 text-sm text-ink-soft">No review or approval recorded.</p>
      ) : (
        <ul className="mt-1 space-y-2">
          {approvals.map((a) => (
            <li key={a.id} className="text-sm text-ink">
              <span className="font-semibold">{approvalSentence(a)}</span>
              {a.effective_from ? <span> - effective from {fmtDateTime(a.effective_from)}</span> : null}
              <span className="block text-xs text-ink-soft">
                Recorded by {a.recorded_by} at {fmtDateTime(a.recorded_at)}
                {a.evidence_location ? ` - signed copy: ${a.evidence_location}` : ''}. Recording evidence gives the recorder no approval
                authority.
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-ink-faint">Activations</p>
      {activations.length === 0 ? (
        <p className="mt-1 text-sm text-ink-soft">Not activated at any issuing authority you can see.</p>
      ) : (
        <ul className="mt-1 space-y-2">
          {activations.map((a) => (
            <li key={a.id} className="text-sm text-ink">
              <span className="font-semibold">
                {a.site_name}
                {a.unit_name ? ` - ${a.unit_name}` : ''}
              </span>{' '}
              from {fmtDateTime(a.effective_from)}{' '}
              <Badge tone={a.in_force ? 'ok' : a.scheduled ? 'warn' : 'neutral'}>{a.in_force ? 'In force' : a.scheduled ? 'Scheduled' : 'Superseded'}</Badge>
              <span className="block text-xs text-ink-soft">
                Activated by {a.activated_by} at {fmtDateTime(a.activated_at)}: {a.reason}
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-3 text-xs text-ink-soft">
        New permits take the version active for their issuing authority when they are raised. Permits already raised keep their version.
      </p>
    </Card>
  )
}

/** The implementation-capability check: what stops this version being activated. */
export function CapabilityCard({ check }: { check: PolicyEvidence['capability_check'] }) {
  return (
    <Card>
      <SectionTitle aside={<Badge tone={check.ok ? 'ok' : 'bad'}>{check.ok ? 'Passes' : `${check.blockers.length} to clear`}</Badge>}>
        Can the software honour this version?
      </SectionTitle>
      {check.ok ? (
        <p className="text-sm text-ink">
          Every class, route and value this version enables is implemented, and its role model and gas acceptance model are ones the
          software enforces.
        </p>
      ) : (
        <ul className="space-y-2">
          {check.blockers.map((b, i) => (
            <li key={`${b.code}-${i}`} className="text-sm text-ink">
              <span className="font-semibold text-bad">{blockerTitle(b.code)}: </span>
              {b.message}
            </li>
          ))}
        </ul>
      )}
      {check.warnings.length > 0 ? (
        <>
          <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-ink-faint">Stays blocked (does not stop activation)</p>
          <ul className="mt-1 space-y-1">
            {check.warnings.map((w, i) => (
              <li key={`${w.code}-${i}`} className="text-sm text-ink-soft">
                {w.message}
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </Card>
  )
}
